import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { saveAs } from 'file-saver';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ShieldCheck, FileDown, Loader2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RANGES = [30, 90, 365, 1095];

/** Compliance view: every quote-gate "Factors reviewed" acknowledgement across all estimates, filterable + PDF export. */
export function QuoteGateAcksCard() {
  const [days, setDays] = useState(365);
  const [q, setQ] = useState('');
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      setData(null);
      axios.get(`${API_URL}/api/compliance/quote-gate-acks`, { params: { days, q: q || undefined }, withCredentials: true })
        .then((r) => setData(r.data)).catch(() => setData({ error: true, rows: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [days, q]);
  const exportPdf = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API_URL}/api/compliance/quote-gate-acks.pdf`, { params: { days, q: q || undefined }, withCredentials: true, responseType: 'blob' });
      saveAs(r.data, `Quote_Gate_Acknowledgements_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch { toast.error('Could not export acknowledgements'); } finally { setExporting(false); }
  };
  const rows = data?.rows || [];
  return (
    <Card className="border-violet-200" data-testid="quote-gate-acks-card">
      <CardContent className="p-5">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <ShieldCheck className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-semibold text-[#0F2040]">Quote gate acknowledgements</h3>
          <span className="text-xs text-slate-400">· {data ? data.count : '…'} across all estimates</span>
          <div className="ml-auto flex items-center gap-2">
            <div className="inline-flex rounded-md border border-slate-200 overflow-hidden" data-testid="quote-gate-acks-range">
              {RANGES.map((d) => (
                <button key={d} type="button" onClick={() => setDays(d)} data-testid={`quote-gate-acks-range-${d}`}
                  className={`px-2 py-0.5 text-[11px] font-medium ${days === d ? 'bg-[#0F2040] text-white' : 'text-slate-600 hover:bg-slate-50'}`}>{d === 1095 ? '3y' : d === 365 ? '1y' : `${d}d`}</button>
              ))}
            </div>
            <div className="relative">
              <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Estimate, reviewer, factor, justification…" className="h-7 w-64 text-xs pl-6" data-testid="quote-gate-acks-search" />
            </div>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={exportPdf} disabled={exporting} data-testid="quote-gate-acks-export-btn">
              {exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><FileDown className="w-3 h-3 mr-1" /> Export PDF</>}
            </Button>
          </div>
        </div>
        <p className="text-xs text-slate-500 mb-3">Every stakeholder send where the sender confirmed “Factors reviewed” on a deal flagged above the portfolio benchmark — who, when, which factors, and the justification. The export honours the active range and search.</p>
        {!data && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
        {data?.error && <p className="text-xs text-rose-600">Could not load acknowledgements.</p>}
        {data && !data.error && rows.length === 0 && <p className="text-xs text-slate-400" data-testid="quote-gate-acks-empty">No acknowledgements match.</p>}
        {rows.length > 0 && (
          <div className="rounded-lg border border-slate-200 overflow-x-auto">
            <table className="w-full text-xs" data-testid="quote-gate-acks-table">
              <thead className="bg-slate-50 text-slate-500"><tr>
                <th className="text-left px-2 py-1.5 font-medium">Acknowledged</th><th className="text-left px-2 py-1.5 font-medium">Estimate</th>
                <th className="text-left px-2 py-1.5 font-medium">Reviewed by</th><th className="text-left px-2 py-1.5 font-medium">Recipients</th>
                <th className="text-left px-2 py-1.5 font-medium">Flagged factors</th><th className="text-left px-2 py-1.5 font-medium">Justification</th>
              </tr></thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.batch_id} className="border-t border-slate-100" data-testid={`quote-gate-ack-row-${r.batch_id}`}>
                    <td className="px-2 py-1.5 whitespace-nowrap text-slate-600">{r.at ? new Date(r.at).toLocaleString() : '—'}</td>
                    <td className="px-2 py-1.5 font-medium text-[#0F2040]">{r.estimate_name}</td>
                    <td className="px-2 py-1.5">{r.by}</td>
                    <td className="px-2 py-1.5 text-slate-600" title={r.recipients.join(', ')}>{r.recipients.length}</td>
                    <td className="px-2 py-1.5"><div className="flex flex-wrap gap-1">{r.flags.map((f) => <span key={f.label} className="text-[10px] px-1.5 rounded border bg-violet-50 border-violet-200 text-violet-800">{f.label} ×{Number(f.multiplier).toFixed(2)}</span>)}</div></td>
                    <td className="px-2 py-1.5 text-slate-600 italic">{r.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
